import React, { useState } from 'react';
import { BarChart2, PieChart, Download, Info } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения детальной разбивки затрат по проекту Big Data
 */
const CostBreakdown = ({ costBreakdown, results, formData }) => {
  const [activeTab, setActiveTab] = useState('table');
  
  if (!costBreakdown || !results) {
    return null;
  }

  // Вспомогательная функция для форматирования валюты
  const formatCurrencyWithSymbol = (value) => {
    const currencySymbol = getCurrencySymbol(formData.currency);
    const formattedValue = formatNumber(value, 2);
    return `${formattedValue} ${currencySymbol}`;
  };

  // Получение символа валюты
  const getCurrencySymbol = (currencyCode) => {
    switch (currencyCode.toLowerCase()) {
      case 'usd': return '$';
      case 'eur': return '€';
      case 'rub': return '₽';
      case 'gbp': return '£';
      default: return currencyCode.toUpperCase();
    }
  };

  // Подготовка данных для визуализации
  const pieChartData = [
    {
      name: 'Хранение',
      value: costBreakdown.infrastructure.storage,
      percentage: (costBreakdown.infrastructure.storage / results.totalCost) * 100,
      color: 'bg-blue-400 dark:bg-blue-600'
    },
    {
      name: 'Обработка',
      value: costBreakdown.infrastructure.processing,
      percentage: (costBreakdown.infrastructure.processing / results.totalCost) * 100,
      color: 'bg-blue-600 dark:bg-blue-800'
    },
    {
      name: 'Интеграция',
      value: costBreakdown.infrastructure.dataIntegration,
      percentage: (costBreakdown.infrastructure.dataIntegration / results.totalCost) * 100,
      color: 'bg-blue-300 dark:bg-blue-500'
    },
    {
      name: 'Персонал',
      value: costBreakdown.personnel.total,
      percentage: (costBreakdown.personnel.total / results.totalCost) * 100,
      color: 'bg-green-500 dark:bg-green-700'
    }
  ];

  // Добавляем дополнительные затраты, если они есть
  if (costBreakdown.additional.maintenance > 0) {
    pieChartData.push({
      name: 'Обслуживание',
      value: costBreakdown.additional.maintenance,
      percentage: (costBreakdown.additional.maintenance / results.totalCost) * 100,
      color: 'bg-orange-500 dark:bg-orange-700'
    });
  }

  if (costBreakdown.additional.training > 0) {
    pieChartData.push({
      name: 'Обучение',
      value: costBreakdown.additional.training,
      percentage: (costBreakdown.additional.training / results.totalCost) * 100,
      color: 'bg-purple-400 dark:bg-purple-600'
    });
  }

  if (costBreakdown.additional.licensing > 0) {
    pieChartData.push({
      name: 'Лицензии',
      value: costBreakdown.additional.licensing,
      percentage: (costBreakdown.additional.licensing / results.totalCost) * 100,
      color: 'bg-purple-600 dark:bg-purple-800'
    });
  }

  if (costBreakdown.additional.consulting > 0) {
    pieChartData.push({
      name: 'Консалтинг',
      value: costBreakdown.additional.consulting,
      percentage: (costBreakdown.additional.consulting / results.totalCost) * 100,
      color: 'bg-purple-300 dark:bg-purple-500'
    });
  }

  // Непредвиденные расходы
  pieChartData.push({
    name: 'Непредвиденные расходы',
    value: costBreakdown.summary.contingency,
    percentage: (costBreakdown.summary.contingency / results.totalCost) * 100,
    color: 'bg-red-500 dark:bg-red-700'
  });

  // Сортируем данные по убыванию стоимости для лучшей визуализации
  pieChartData.sort((a, b) => b.value - a.value);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart2 className="w-5 h-5 mr-2 text-indigo-500" />
          Детализация затрат
        </h3>

        <div className="flex space-x-2">
          <Button
            size="sm"
            onClick={() => setActiveTab('table')}
            variant={activeTab === 'table' ? 'primary' : 'outline'}
            aria-pressed={activeTab === 'table'}
          >
            Таблица
          </Button>
          <Button
            size="sm"
            onClick={() => setActiveTab('chart')}
            variant={activeTab === 'chart' ? 'primary' : 'outline'}
            aria-pressed={activeTab === 'chart'}
            icon={<PieChart className="w-4 h-4" />}
          >
            Диаграмма
          </Button>
        </div>
      </div>

      {/* Информационная подсказка */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded p-3 mb-4 flex items-start">
        <Info className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p>Полная разбивка затрат на проект с указанием стоимости каждого компонента.</p>
        </div>
      </div>

      {/* Содержимое вкладок */}
      {activeTab === 'table' && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 cost-breakdown-table">
          <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
            <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700">
              <tr>
                <th scope="col" className="px-4 py-3 font-semibold">Категория</th>
                <th scope="col" className="px-4 py-3 font-semibold text-right">Стоимость</th>
                <th scope="col" className="px-4 py-3 font-semibold text-right">% от общей</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {/* Инфраструктура - заголовок */}
              <tr className="bg-gray-50 dark:bg-gray-800">
                <td colSpan="3" className="px-4 py-2 font-medium text-indigo-600 dark:text-indigo-400">ИНФРАСТРУКТУРА</td>
              </tr>
              
              {/* Хранение данных */}
              <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-4 py-2 pl-8">Хранение данных</td>
                <td className="px-4 py-2 text-right">{formatCurrencyWithSymbol(costBreakdown.infrastructure.storage)}</td>
                <td className="px-4 py-2 text-right">{formatNumber((costBreakdown.infrastructure.storage / results.totalCost) * 100, 1)}%</td>
              </tr>
              
              {/* Обработка данных */}
              <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-4 py-2 pl-8">Обработка данных</td>
                <td className="px-4 py-2 text-right">{formatCurrencyWithSymbol(costBreakdown.infrastructure.processing)}</td>
                <td className="px-4 py-2 text-right">{formatNumber((costBreakdown.infrastructure.processing / results.totalCost) * 100, 1)}%</td>
              </tr>
              
              {/* Интеграция данных */}
              <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-4 py-2 pl-8">Интеграция источников</td>
                <td className="px-4 py-2 text-right">{formatCurrencyWithSymbol(costBreakdown.infrastructure.dataIntegration)}</td>
                <td className="px-4 py-2 text-right">{formatNumber((costBreakdown.infrastructure.dataIntegration / results.totalCost) * 100, 1)}%</td>
              </tr>
              
              {/* Итого по инфраструктуре */}
              <tr className="bg-gray-100 dark:bg-gray-700/70 font-medium">
                <td className="px-4 py-2">Итого по инфраструктуре</td>
                <td className="px-4 py-2 text-right">{formatCurrencyWithSymbol(costBreakdown.infrastructure.total)}</td>
                <td className="px-4 py-2 text-right">{formatNumber((costBreakdown.infrastructure.total / results.totalCost) * 100, 1)}%</td>
              </tr>
              
              {/* Персонал - заголовок */}
              <tr className="bg-gray-50 dark:bg-gray-800">
                <td colSpan="3" className="px-4 py-2 font-medium text-green-600 dark:text-green-400">ПЕРСОНАЛ</td>
              </tr>
              
              {/* Персонал - стоимость */}
              <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-4 py-2 pl-8">Команда проекта</td>
                <td className="px-4 py-2 text-right">{formatCurrencyWithSymbol(costBreakdown.personnel.total)}</td>
                <td className="px-4 py-2 text-right">{formatNumber((costBreakdown.personnel.total / results.totalCost) * 100, 1)}%</td>
              </tr>
              
              {/* Дополнительные расходы - заголовок */}
              {costBreakdown.additional.total > 0 && (
                <>
                  <tr className="bg-gray-50 dark:bg-gray-800">
                    <td colSpan="3" className="px-4 py-2 font-medium text-purple-600 dark:text-purple-400">ДОПОЛНИТЕЛЬНЫЕ РАСХОДЫ</td>
                  </tr>
                  
                  {/* Обслуживание */}
                  {costBreakdown.additional.maintenance > 0 && (
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-2 pl-8">Обслуживание системы</td>
                      <td className="px-4 py-2 text-right">{formatCurrencyWithSymbol(costBreakdown.additional.maintenance)}</td>
                      <td className="px-4 py-2 text-right">{formatNumber((costBreakdown.additional.maintenance / results.totalCost) * 100, 1)}%</td>
                    </tr>
                  )}
                  
                  {/* Обучение */}
                  {costBreakdown.additional.training > 0 && (
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-2 pl-8">Обучение персонала</td>
                      <td className="px-4 py-2 text-right">{formatCurrencyWithSymbol(costBreakdown.additional.training)}</td>
                      <td className="px-4 py-2 text-right">{formatNumber((costBreakdown.additional.training / results.totalCost) * 100, 1)}%</td>
                    </tr>
                  )}
                  
                  {/* Лицензии */}
                  {costBreakdown.additional.licensing > 0 && (
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-2 pl-8">Лицензирование ПО</td>
                      <td className="px-4 py-2 text-right">{formatCurrencyWithSymbol(costBreakdown.additional.licensing)}</td>
                      <td className="px-4 py-2 text-right">{formatNumber((costBreakdown.additional.licensing / results.totalCost) * 100, 1)}%</td>
                    </tr>
                  )}
                  
                  {/* Консалтинг */}
                  {costBreakdown.additional.consulting > 0 && (
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-2 pl-8">Консалтинговые услуги</td>
                      <td className="px-4 py-2 text-right">{formatCurrencyWithSymbol(costBreakdown.additional.consulting)}</td>
                      <td className="px-4 py-2 text-right">{formatNumber((costBreakdown.additional.consulting / results.totalCost) * 100, 1)}%</td>
                    </tr>
                  )}
                  
                  {/* Итого по дополнительным расходам */}
                  <tr className="bg-gray-100 dark:bg-gray-700/70 font-medium">
                    <td className="px-4 py-2">Итого по дополнительным расходам</td>
                    <td className="px-4 py-2 text-right">{formatCurrencyWithSymbol(costBreakdown.additional.total)}</td>
                    <td className="px-4 py-2 text-right">{formatNumber((costBreakdown.additional.total / results.totalCost) * 100, 1)}%</td>
                  </tr>
                </>
              )}
              
              {/* Непредвиденные расходы */}
              <tr className="bg-gray-50 dark:bg-gray-800">
                <td colSpan="3" className="px-4 py-2 font-medium text-red-600 dark:text-red-400">НЕПРЕДВИДЕННЫЕ РАСХОДЫ</td>
              </tr>
              
              <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-4 py-2 pl-8">Резерв на непредвиденные расходы</td>
                <td className="px-4 py-2 text-right">{formatCurrencyWithSymbol(costBreakdown.summary.contingency)}</td>
                <td className="px-4 py-2 text-right">{formatNumber((costBreakdown.summary.contingency / results.totalCost) * 100, 1)}%</td>
              </tr>
              
              {/* ИТОГО */}
              <tr className="bg-indigo-100 dark:bg-indigo-900/50 font-bold">
                <td className="px-4 py-3">ИТОГО ПО ПРОЕКТУ</td>
                <td className="px-4 py-3 text-right">{formatCurrencyWithSymbol(costBreakdown.summary.total)}</td>
                <td className="px-4 py-3 text-right">100%</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Визуализация затрат в виде диаграммы */}
      {activeTab === 'chart' && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          {/* Гистограмма затрат */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Распределение затрат по категориям</h4>
            <div className="space-y-2">
              {pieChartData.map((item, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <div className={`w-4 h-4 rounded-sm ${item.color} flex-shrink-0`}></div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 flex-grow">{item.name}</div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrencyWithSymbol(item.value)}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 w-16 text-right">{formatNumber(item.percentage, 1)}%</div>
                  {/* Визуализация процента в виде полоски */}
                  <div className="w-40 bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                    <div className={`h-2.5 rounded-full ${item.color}`} style={{ width: `${item.percentage}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Кнопка экспорта диаграммы (опционально) */}
      <div className="mt-4 flex justify-end">
        <Button
          size="sm"
          variant="outline"
          icon={<Download className="w-4 h-4" />}
          onClick={() => window.print()} // Упрощенная версия для печати, в реальном применении здесь был бы полноценный экспорт
        >
          Экспортировать
        </Button>
      </div>
    </div>
  );
};

export default CostBreakdown;